import { cn } from "@msuite/ui/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type { KeyboardEvent, PropsWithChildren } from "react";

/** Props Interface */
interface DashboardTileListItemProps extends PropsWithChildren, VariantProps<typeof item> {
	/** What activating the row does — a click, or Enter and Space while it has the focus. */
	onClick?: () => void;
	"data-cy"?: string;
}

/**
 * A row's children carry its action — the tasks tile's toggle, the absence row's menu
 * trigger — so the row itself cannot be a `<button>`: a button may not contain another one,
 * and React warns on every render where it does. The row is a focusable `role="button"`
 * element instead, and Enter or Space on it activates the first button it holds.
 */
export function DashboardTileListItem({
	children,
	orientation,
	onClick,
	"data-cy": dataCy,
}: DashboardTileListItemProps) {
	/** Functions */
	function handleOnKeyDown(e: KeyboardEvent<HTMLDivElement>) {
		// A key pressed on a control inside the row is that control's to handle; the row
		// steers only the keys aimed at itself, or an arrow on the menu trigger would open
		// the menu and move the focus to the next row at once.
		if (e.target !== e.currentTarget) return;
		if (e.code === "Tab") return;
		e.preventDefault();
		if (["ArrowDown", "ArrowRight"].includes(e.code)) {
			focusElement(e.currentTarget.nextElementSibling);
		} else if (["ArrowUp", "ArrowLeft"].includes(e.code)) {
			focusElement(e.currentTarget.previousElementSibling);
		} else if (["Enter", "Space"].includes(e.code)) {
			if (onClick) return onClick();
			e.currentTarget.querySelector("button")?.click();
		}
	}

	/** Computed */
	const itemClasses = item({ orientation });

	/** Render */
	return (
		// biome-ignore lint/a11y/useSemanticElements: the row holds its own action button, and a <button> may not contain one
		<div
			role="button"
			tabIndex={0}
			className={cn(itemClasses)}
			onClick={onClick}
			data-cy={dataCy}
			onKeyDown={handleOnKeyDown}
		>
			{children}
		</div>
	);
}

function focusElement(element: Element | null) {
	if (element instanceof HTMLElement) element.focus();
}

const item = cva(
	[
		"dark:focus:bg-accent-950/20 focus:bg-accent-50",
		"not-last:border-b border-dotted border-color py-2 pr-2 pl-6",
		"cursor-pointer hover:bg-primary-50 hover:dark:bg-primary-950",
	],
	{
		variants: {
			orientation: {
				horizontal: "h-stack",
				vertical: "v-stack gap-0",
			},
		},
		defaultVariants: {
			orientation: "horizontal",
		},
	},
);
